import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { fmt } from '../chart-utils';

export interface StackedSeries {
  label: string;
  color: string;
  data: number[];
}

const COLOR_GRID = 'rgba(255,255,255,0.08)';
const COLOR_LABEL = 'rgba(255,255,255,0.68)';
const COLOR_AXIS_LABEL = 'rgba(255,255,255,0.82)';

@Component({
  selector: 'app-stacked-bar-chart',
  templateUrl: './stacked-bar-chart.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StackedBarChart {
  readonly labels = input<string[]>([]);
  readonly series = input<StackedSeries[]>([]);

  private readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvas');
  private readonly destroyRef = inject(DestroyRef);
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    effect(() => {
      const canvasEl = this.canvasRef();
      const labels = this.labels();
      const series = this.series();
      if (!canvasEl || !labels.length) return;
      const canvas = canvasEl.nativeElement;
      if (canvas.getBoundingClientRect().width === 0) return;
      this.draw(canvas, labels, series);
    });

    afterNextRender(() => {
      const canvasEl = this.canvasRef();
      if (!canvasEl) return;
      const canvas = canvasEl.nativeElement;
      this.draw(canvas, this.labels(), this.series());
      this.resizeObserver = new ResizeObserver(() =>
        this.draw(canvas, this.labels(), this.series()),
      );
      this.resizeObserver.observe(canvas.parentElement ?? canvas);
    });

    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
  }

  private draw(canvas: HTMLCanvasElement, labels: string[], series: StackedSeries[]): void {
    const ctx = canvas.getContext('2d');
    if (!ctx || !labels.length || !series.length) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const pad = { top: 22, right: 20, bottom: 42, left: 46 };
    const chartW = w - pad.left - pad.right;
    const chartH = h - pad.top - pad.bottom;
    const groupWidth = chartW / labels.length;
    const barWidth = Math.min(48, groupWidth * 0.5);

    ctx.clearRect(0, 0, w, h);
    ctx.font = '12px Inter, sans-serif';

    // Grid lines + Y axis labels (0%–100%)
    for (let i = 0; i <= 4; i++) {
      const y = pad.top + (chartH / 4) * i;
      ctx.beginPath();
      ctx.strokeStyle = COLOR_GRID;
      ctx.moveTo(pad.left, y);
      ctx.lineTo(w - pad.right, y);
      ctx.stroke();
      ctx.fillStyle = COLOR_LABEL;
      ctx.fillText(fmt(100 - i * 25) + '%', 4, y + 4);
    }

    // Stacked bars
    labels.forEach((label, i) => {
      const centerX = pad.left + groupWidth * i + groupWidth / 2;
      const barX = Math.round(centerX - barWidth / 2);
      let cumY = pad.top + chartH;

      series.forEach((s) => {
        const val = s.data[i] ?? 0;
        const segH = (val / 100) * chartH;
        if (segH < 0.5) return;
        cumY -= segH;
        ctx.fillStyle = s.color;
        ctx.fillRect(barX, Math.round(cumY), Math.round(barWidth), Math.round(segH) - 1);
      });

      ctx.fillStyle = COLOR_AXIS_LABEL;
      ctx.textAlign = 'center';
      ctx.fillText(label, centerX, h - 18);
      ctx.textAlign = 'start';
    });
  }
}
